import classes from "./media-manager.module.scss";
import { useState } from "react";
import { useAppDispatch } from "@common-reducers/hooks/store.hook";
import { mediaManager } from "@common-reducers/MediaManagerReducer";
import { useAppSelector } from "@common/reducers/hooks/store.hook";
// react-doc-viewer (react-pdf/pdfjs + styled-components stack, ~228KB) is split
// out of app.js via a lazy WAR bundle; the boundary owns the renderer composition
// (built-ins + the custom webp renderer) so this file no longer imports the lib.
import LazyDocViewer from "./lazy-doc-viewer";
import CloseIcon from '@mui/icons-material/Close';
import { BiDownload, BiTrash } from "react-icons/bi";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { AiFillFileZip } from "react-icons/ai";
import { Tooltip, Button } from "@mui/material";
import { loadJszip } from '@extension/utils/lazy-lib';
import JSZipUtils from "jszip-utils";
import FileSaver from 'file-saver';
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import { IconButton } from '@mui/material';
import FilesVersions from "@common-components/files-versions/files-versions";
import MediaManagerHeader from "@common-views/media-manager-header/media-manager-header";
import MediaManagerSideBar from "@common-views/media-manager-sidebar/media-manager-sidebar";
import { getFilesCurrentIndexAndVersionCurrentIndex, getFileUrl, getMediaManagerFileList } from "@common-reducers/MediaManagerSelectors";
import { deleteFile } from "@common-reducers/MediaManagerThunks";
import MediaMangerEdit from "@common-components/media-manager-edit/media-manager-edit";
import { cellValueSelector } from "@common-reducers/TaskSelectors";

// Skeleton shown by react-doc-viewer while a document loads.
function DocLoadingSkeleton() {
    return (
        <div className={classes.previewSkeleton} aria-hidden="true">
            <div className={classes.skeletonPage} />
        </div>
    );
}

type MediaFile = { url: string; filename: string; _id?: string };

export default function MediaManager() {
    const dispatch = useAppDispatch()
    const fileList = useAppSelector(getMediaManagerFileList);
    const taskId = useAppSelector((state) => state.MediaManagerReducer.taskId);
    const columnId = useAppSelector((state) => state.MediaManagerReducer.columnId);
    const cellValue = useAppSelector((state) => cellValueSelector(state, taskId, columnId))
    const isViewOnly = useAppSelector((state) => state.MediaManagerReducer.isViewOnly);
    const isMediaManagerShown = useAppSelector((state) => state.MediaManagerReducer.shown);
    const isShowVersions = useAppSelector((state) => state.MediaManagerReducer.showVersionTab);
    const isEditMode = useAppSelector((state) => state.MediaManagerReducer.showEditTab);
    const { filesCurrentIndex } = useAppSelector(getFilesCurrentIndexAndVersionCurrentIndex);
    const fileUrl = useAppSelector(getFileUrl);
    const filesLength = fileList?.length ?? 0;
    const hasMultipleFiles = filesLength > 1;
    const currentFileShown: MediaFile | undefined = fileList?.[filesCurrentIndex];
    const fileName = currentFileShown?.filename;

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    function downloadFile(file: MediaFile | undefined) {
        if (!file?.url) return;
        FileSaver.saveAs(file.url, file.filename);
    }

    async function downloadZip(files: MediaFile[]) {
        const JSZip = await loadJszip();
        const zip = new JSZip();
        let count = 0;
        files.forEach(function (file: MediaFile) {
            var filename = file.filename;
            JSZipUtils.getBinaryContent(file.url, function (err: Error | null, data: unknown) {
                if (err) {
                    throw err;
                }
                zip.file(filename, data as ArrayBuffer, { binary: true });
                count++;
                if (count === files.length) {
                    zip.generateAsync({ type: 'blob' }).then(function (content) {
                        FileSaver.saveAs(content, 'download.zip');
                    });
                }
            });
        });
    }

    function switchFile(type: 'prev' | 'next') {
        if (filesLength <= 1) return;
        const nextIndex = type === 'prev'
            ? (filesCurrentIndex === 0 ? filesLength - 1 : filesCurrentIndex - 1)
            : (filesCurrentIndex !== filesLength - 1 ? filesCurrentIndex + 1 : 0);
        const filesStateUpdate = {
            index: nextIndex,
            isVersionPreview: false,
            versionIndex: 0,
        };
        if (fileList[nextIndex]?._id) {
            dispatch(mediaManager.setTaskId(fileList[nextIndex]._id));
        }
        dispatch(mediaManager.switchFile(filesStateUpdate));
    }

    function clearFile() {
        setConfirmDeleteOpen(false);
        const current = cellValue ?? [];
        let fileListFiltered = current.filter((_: unknown, index: number) => index !== filesCurrentIndex);
        const isNowEmpty = fileListFiltered.length === 0;
        // Move the preview off the file being deleted BEFORE the store shrinks,
        // otherwise the index can point past the end of the shortened list.
        isNowEmpty ? closeMediaManager() : switchFile('next');
        dispatch(deleteFile(isNowEmpty ? undefined : fileListFiltered))
    }

    function closeMediaManager() {
        dispatch(
            mediaManager.closeDialog({ shown: false, taskId: null })
        );
    }

    function checkKey(e: React.KeyboardEvent) {
        if (e.key === "ArrowRight") {
            switchFile('next')
        }
        if (e.key === "ArrowLeft") {
            switchFile('prev')
        }
    }

    return (
        <Dialog onKeyDown={checkKey} onClose={closeMediaManager} fullWidth maxWidth="xl" open={isMediaManagerShown}
            PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden', height: '86vh' } }}>
            <DialogContent sx={{ padding: "0px", overflow: 'hidden' }}>
                <div id="media-manager" className={classes.mediaManager}>
                    <div className={classes.mediaManagerHeader}>
                        <MediaManagerHeader file={currentFileShown ?? {}} fileName={fileName} />
                        <Tooltip title="Close" arrow disableInteractive>
                            <IconButton aria-label="close media manager" onClick={closeMediaManager} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                    <div className={classes.mediaManagerBody}>
                        {fileUrl && (
                            <div className={classes.docPreviewContainer}>
                                <div className={hasMultipleFiles ? classes.arrowContainer : classes.disabled}>
                                    <span className={classes.previousArrow} onClick={() => switchFile('prev')}>
                                        <Tooltip title={hasMultipleFiles ? 'Previous file' : 'No other files'} arrow placement="right" disableInteractive>
                                            <IconButton className={classes.navBtn} size="large">
                                                <ChevronLeftIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </span>
                                    <span className={classes.nextArrow} onClick={() => switchFile('next')}>
                                        <Tooltip title={hasMultipleFiles ? 'Next file' : 'No other files'} arrow placement="left" disableInteractive>
                                            <IconButton className={classes.navBtn} size="large">
                                                <ChevronRightIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </span>
                                </div>
                                <LazyDocViewer
                                    theme={{ disableThemeScrollbar: true }}
                                    className={classes.docViewer}
                                    config={{
                                        header: {
                                            disableHeader: true,
                                            disableFileName: true,
                                            retainURLParams: true,
                                        },
                                        loadingRenderer: {
                                            overrideComponent: DocLoadingSkeleton,
                                        },
                                    }}
                                    documents={[{ uri: fileUrl }]}
                                />
                                <div className={classes.actions}>
                                    <Tooltip title="Download file" arrow placement="top" disableInteractive>
                                        <IconButton size="small" onClick={() => downloadFile(currentFileShown)}>
                                            <BiDownload />
                                        </IconButton>
                                    </Tooltip>
                                    {hasMultipleFiles && (
                                        <Tooltip title={'Download ' + filesLength + ' files as zip'} arrow placement="top" disableInteractive>
                                            <IconButton size="small" onClick={() => downloadZip(fileList)}>
                                                <AiFillFileZip />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {!isViewOnly && (
                                        <Tooltip title="Delete file" arrow placement="top" disableInteractive>
                                            <IconButton size="small" className={classes.deleteBtn} onClick={() => setConfirmDeleteOpen(true)}>
                                                <BiTrash />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        )}
                        {!fileUrl && (
                            <div className={classes.emptyPreview}>No file to preview</div>
                        )}
                        {isShowVersions && taskId && columnId && (
                            <div className={classes.versionsWrapper}>
                                <FilesVersions />
                            </div>
                        )}
                        {isEditMode && (
                            <div className={classes.editWrapper}>
                                <MediaMangerEdit />
                            </div>
                        )}
                        {taskId && columnId && <MediaManagerSideBar />}
                    </div>
                </div>
            </DialogContent>

            <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}
                PaperProps={{ sx: { borderRadius: '12px', maxWidth: 380 } }}>
                <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Delete file?</DialogTitle>
                <DialogContent sx={{ fontSize: 14, color: 'var(--color-text-muted)' }}>
                    {fileName ? `"${fileName}" will be removed from this task. This can't be undone.` : `This file will be removed from this task. This can't be undone.`}
                </DialogContent>
                <DialogActions sx={{ padding: '12px 20px' }}>
                    <Button onClick={() => setConfirmDeleteOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={clearFile} color="error" variant="contained" disableElevation sx={{ textTransform: 'none' }}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}
